/**
 * "Connect a source" (Files screen 6): the whole connector catalog as tiles,
 * split by how Doppler reads it — drives and data rooms it keeps a searchable
 * copy of, and email/CRM it reads live. Each tile's state comes from
 * `connectTileState`; the OAuth hand-off is the caller's `onConnect`.
 */
import { Upload, X } from "lucide-react";
import React, { useEffect } from "react";
import { DATA_SOURCES, type DataSource } from "../../config/dataSources";
import { useDataSourceStatus } from "../../hooks/useDataSourceStatus";
import { connectTileState, type TileTone } from "./connectTileState";

interface ConnectSourceDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConnect: (dataSource: DataSource) => void;
	onUpload: () => void;
}

const DRIVE_CATEGORIES: DataSource["category"][] = [
	"enterprise",
	"cloud-storage",
	"file-sharing",
];

const TONE_CLASS: Record<TileTone, string> = {
	ok: "text-[#067647]",
	warn: "text-[#b54708]",
	muted: "text-text-muted",
	off: "text-gray-400",
};

const tileClass =
	"flex items-center gap-2.5 rounded-[10px] border border-gray-200 p-3";
const logoClass =
	"flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-lg bg-[#f2f2f2]";
const buttonClass =
	"inline-flex h-8 flex-shrink-0 items-center rounded-lg border px-3 text-[12.5px] font-medium";

const SourceTile: React.FC<{
	dataSource: DataSource;
	tone: TileTone;
	status: string;
	action: { label: string; primary: boolean } | null;
	onAction: () => void;
}> = ({ dataSource, tone, status, action, onAction }) => (
	<div className={`${tileClass} ${tone === "off" ? "opacity-70" : ""}`}>
		<span className={logoClass}>
			<img
				src={dataSource.icon}
				alt=""
				aria-hidden
				className="max-h-5 max-w-5 object-contain"
			/>
		</span>
		<div className="min-w-0 flex-1">
			<div className="truncate text-[13.5px] font-semibold text-gray-900">
				{dataSource.name}
			</div>
			<div className={`mt-0.5 truncate text-[11.5px] ${TONE_CLASS[tone]}`}>
				{status}
			</div>
		</div>
		{action && (
			<button
				type="button"
				onClick={onAction}
				aria-label={`${action.label} ${dataSource.name}`}
				className={`${buttonClass} ${
					action.primary
						? "border-[#004C4B] bg-[#004C4B] font-semibold text-white hover:bg-[#003a3a]"
						: "border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
				}`}
			>
				{action.label}
			</button>
		)}
	</div>
);

const ConnectSourceDialog: React.FC<ConnectSourceDialogProps> = ({
	isOpen,
	onClose,
	onConnect,
	onUpload,
}) => {
	const { bySystem, now, isLocked, needsReauthorization } =
		useDataSourceStatus(isOpen);

	useEffect(() => {
		if (!isOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const renderTile = (dataSource: DataSource) => {
		const state = connectTileState({
			dataSource,
			row: bySystem[dataSource.sourceSystem.toLowerCase()],
			now,
			locked: isLocked(dataSource),
			needsReauthorization: needsReauthorization(dataSource),
		});
		return (
			<SourceTile
				key={dataSource.id}
				dataSource={dataSource}
				{...state}
				onAction={() => {
					// Re-checked at click time: the connections read can land
					// after the dialog paints.
					if (!isLocked(dataSource) || needsReauthorization(dataSource)) {
						onConnect(dataSource);
					}
				}}
			/>
		);
	};

	const drives = DATA_SOURCES.filter((s) =>
		DRIVE_CATEGORIES.includes(s.category),
	);
	const live = DATA_SOURCES.filter((s) => !DRIVE_CATEGORIES.includes(s.category));

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4"
			onClick={onClose}
			role="presentation"
		>
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby="connect-source-title"
				onClick={(e) => e.stopPropagation()}
				className="flex max-h-[90vh] w-full max-w-[840px] flex-col overflow-hidden rounded-[14px] bg-white shadow-2xl"
			>
				<div className="flex items-start border-b border-[#eef0f2] px-6 pb-4 pt-5">
					<div>
						<h2
							id="connect-source-title"
							className="text-[19px] font-semibold text-gray-900"
						>
							Connect a source
						</h2>
					</div>
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50"
					>
						<X className="h-4 w-4" aria-hidden />
					</button>
				</div>

				<div className="flex min-h-0 flex-col gap-5 overflow-y-auto px-6 py-5">
					<section>
						<h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.09em] text-text-muted">
							Drives and data rooms · Doppler keeps a searchable copy
						</h3>
						<div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
							{drives.map(renderTile)}
							<div className={`${tileClass} border-dashed`}>
								<span className={logoClass}>
									<Upload className="h-4 w-4 text-gray-600" aria-hidden />
								</span>
								<div className="min-w-0 flex-1">
									<div className="text-[13.5px] font-semibold text-gray-900">
										Upload files
									</div>
									<div className="mt-0.5 text-[11.5px] text-text-muted">
										Files or whole folders
									</div>
								</div>
								<button
									type="button"
									onClick={onUpload}
									className={`${buttonClass} border-gray-200 bg-white text-gray-800 hover:bg-gray-50`}
								>
									Upload
								</button>
							</div>
						</div>
					</section>

					<section>
						<h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.09em] text-text-muted">
							Email, chat and CRM · read live, only when asked
						</h3>
						<div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
							{live.map(renderTile)}
						</div>
					</section>
				</div>
			</div>
		</div>
	);
};

export default ConnectSourceDialog;
